import { useState, type FormEvent } from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ArrowLeft, Save, ShieldCheck } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface User {
  id: number
  email: string
  isAdmin: boolean
  allowedUserTypes?: string[]
}

interface Props extends Omit<AppShellProps, 'children'> {
  user: User
  availableUserTypes?: string[]
  isAdminEdit: boolean
  canSetAdmin: boolean
  urls: AppShellProps['urls'] & {
    updateAsAdmin: string
    back: string
  }
  translations: AppShellProps['translations'] & {
    editUser: string
    email: string
    password: string
    passwordConfirmation: string
    admin: string
    save: string
    back: string
    allowedBadgeTypes?: string
    allowedBadgeTypesHint?: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function EditAsAdmin(props: Props) {
  const { user, availableUserTypes = [], isAdminEdit, canSetAdmin, urls, translations: t, ...shell } = props
  const { flash } = usePage<{ flash: { notice?: string; alert?: string } }>().props

  const [form, setForm] = useState({
    email: user.email,
    password: '',
    password_confirmation: '',
    is_admin: user.isAdmin,
    allowed_user_types: user.allowedUserTypes || [],
  })

  const toggleType = (userType: string, checked: boolean) => {
    setForm((prev) => {
      const next = checked
        ? [...prev.allowed_user_types, userType]
        : prev.allowed_user_types.filter((value) => value !== userType)
      return { ...prev, allowed_user_types: next }
    })
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    router.put(urls.updateAsAdmin, {
      user: {
        ...form,
        allowed_user_types: form.is_admin ? [] : form.allowed_user_types,
      },
      id: user.id,
    }, {
      preserveScroll: true,
    })
  }

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={t.editUser} />

      <div className="mx-auto max-w-2xl space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => router.visit(urls.back)}
            className="text-[#91918E] hover:bg-[#F1F1EF]"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#2383E2]" />
            <h1 className="text-2xl font-semibold text-[#37352F]">{t.editUser}</h1>
          </div>
        </div>

        {/* Flash messages */}
        {flash?.notice && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            {flash.notice}
          </div>
        )}
        {flash?.alert && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {flash.alert}
          </div>
        )}

        {/* Admin badge */}
        <div className="flex items-center gap-2 rounded-lg bg-blue-50 px-4 py-2 text-sm text-blue-700">
          <ShieldCheck className="h-4 w-4" />
          Editing user: <strong>{user.email}</strong>
        </div>

        {/* Form */}
        <Card className="border-[#E8E8E5]">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-[#37352F]">{t.editUser}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#37352F]">
                  {t.email}
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="off"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="border-[#E8E8E5] focus-visible:ring-[#2383E2]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#37352F]">
                  {t.password}
                </Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="off"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="border-[#E8E8E5] focus-visible:ring-[#2383E2]"
                  placeholder="Leave blank to keep current"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password_confirmation" className="text-[#37352F]">
                  {t.passwordConfirmation}
                </Label>
                <Input
                  id="password_confirmation"
                  type="password"
                  value={form.password_confirmation}
                  onChange={(e) => setForm({ ...form, password_confirmation: e.target.value })}
                  className="border-[#E8E8E5] focus-visible:ring-[#2383E2]"
                  placeholder="Leave blank to keep current"
                />
              </div>

              {canSetAdmin && (
                <div className="flex items-center gap-3">
                  <input
                    id="is_admin"
                    type="checkbox"
                    checked={form.is_admin}
                    onChange={(e) => setForm({ ...form, is_admin: e.target.checked })}
                    className="h-4 w-4 rounded border-[#E8E8E5] text-[#2383E2] focus:ring-[#2383E2]"
                  />
                  <Label htmlFor="is_admin" className="text-[#37352F]">
                    {t.admin}
                  </Label>
                </div>
              )}

              {!form.is_admin && availableUserTypes.length > 0 && (
                <div className="space-y-2 rounded-lg border border-[#E8E8E5] p-4">
                  <Label className="text-[#37352F]">
                    {t.allowedBadgeTypes || 'Limit this user to these badge types'}
                  </Label>
                  <p className="text-xs text-[#91918E]">
                    {t.allowedBadgeTypesHint || 'Leave all unchecked to allow every badge type.'}
                  </p>
                  <div className="flex flex-col gap-2 pt-1">
                    {availableUserTypes.map((userType) => (
                      <label key={userType} className="flex items-center gap-2 text-sm text-[#37352F]">
                        <input
                          type="checkbox"
                          checked={form.allowed_user_types.includes(userType)}
                          onChange={(e) => toggleType(userType, e.target.checked)}
                          className="h-4 w-4 rounded border-[#E8E8E5] text-[#2383E2] focus:ring-[#2383E2]"
                        />
                        {userType}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <Button
                  type="submit"
                  className="bg-[#2383E2] text-white hover:bg-[#1b6ec2]"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {t.save}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => router.visit(urls.back)}
                  className="border-[#E8E8E5] text-[#37352F] hover:bg-[#F1F1EF]"
                >
                  {t.back}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  )
}
